/**
 * @cfleet/api-client
 *
 * Generated types and API clients from OpenAPI specifications.
 * This package provides type-safe API calls to CFleet backend services.
 *
 * Usage:
 *   import { createCoreClient, createOptimizationClient } from '@cfleet/api-client';
 *   import type { AuthPaths, FleetComponents } from '@cfleet/api-client';
 *
 * With authentication middleware:
 *   import { createAuthenticatedCoreClient, setTokens } from '@cfleet/api-client';
 *
 *   const client = createAuthenticatedCoreClient({
 *     baseUrl: 'http://localhost:8081/api/v1',
 *     onAuthFailure: () => navigate('/login'),
 *   });
 *
 *   // After login, store tokens
 *   setTokens(data.token, data.refresh_token, data.expires_in);
 *
 * @packageDocumentation
 */

import createClient from 'openapi-fetch';

// Re-export all generated types
export * from './generated';

// Re-export shared type definitions
export * from './types';

// Re-export middleware
export * from './middleware';

// Re-export hooks
export * from './hooks';

// Import middleware for authenticated client factories
import { createAuthMiddleware, type AuthMiddlewareConfig } from './middleware';

// Import types for client creation
import type {
  AuthPaths,
  FleetPaths,
  AddressesPaths,
  BusinessPaths,
  CustomerInvitationsPaths,
  RoutesPaths,
  OptimizationPaths,
  OperationsPaths,
  AnalyticsPaths,
  AiAssistantPaths,
  DispatchChatPaths,
} from './generated';
import type {
  DriverAuthPaths,
  DriverProfilePaths,
  DriverRoutesPaths,
  DriverStopsPaths,
  DriverNavigationPaths,
  DriverDispatchPaths,
  DriverHistoryPaths,
} from './generated';
import type {
  CustomerAuthPaths,
  CustomerOrdersReadPaths,
  CustomerOrdersCreatePaths,
  CustomerOrdersActionsPaths,
  CustomerInvoicingPaths,
  CustomerSettingsPaths,
  CustomerOrganizationsPaths,
} from './generated';

export const API_VERSION = '0.0.0';

// ─────────────────────────────────────────────────────────────────────────────
// Client Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuration options for API clients
 */
export interface ClientConfig {
  /** Base URL for the API */
  baseUrl: string;
  /** Default headers to include with every request */
  headers?: Record<string, string>;
}

/**
 * Environment-aware configuration helper
 * Works in both browser (Vite) and React Native environments
 *
 * Note: import.meta is not supported in React Native/Hermes.
 * For web builds, Vite replaces process.env references at build time.
 * For React Native, process.env is available via babel-plugin-transform-inline-environment-variables
 * or can be configured via app.config.js extra field.
 */
function getEnvVar(key: string, fallback: string): string {
  // Check process.env (works in Node, React Native with babel plugin, and Vite with define)
  if (typeof process !== 'undefined' && process.env) {
    const value = (process.env as Record<string, string | undefined>)[key];
    if (value) return value;
  }
  return fallback;
}

/**
 * Default configuration for the core API (cfleet-core-server)
 * Connects to auth, fleet, addresses, routes, operations, analytics endpoints
 *
 * Note: Default uses relative URL '/api/v1' so requests go through Vite proxy (dev)
 * or same-origin (production), avoiding CORS issues.
 */
export const defaultCoreConfig: ClientConfig = {
  baseUrl: getEnvVar('VITE_API_BASE_URL', '/api/v1'),
};

/**
 * Default configuration for the optimization API (optimization-processor)
 * Connects to optimization and real-time endpoints
 *
 * Note: Default uses relative URL '/routing/v1' so requests go through Vite proxy (dev)
 * or same-origin (production), avoiding CORS issues.
 */
export const defaultOptimizationConfig: ClientConfig = {
  baseUrl: getEnvVar('VITE_OPTIMIZATION_API_URL', '/routing/v1'),
};

// ─────────────────────────────────────────────────────────────────────────────
// Combined Path Types for Client Creation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Combined paths for the core API client
 * Includes auth, fleet, addresses, business, customer invitations, routes, operations, analytics, and AI assistant
 */
export type CoreApiPaths = AuthPaths &
  FleetPaths &
  AddressesPaths &
  BusinessPaths &
  CustomerInvitationsPaths &
  RoutesPaths &
  OperationsPaths &
  AnalyticsPaths &
  AiAssistantPaths &
  DispatchChatPaths;

/**
 * Combined paths for driver app API
 * Includes all driver-specific endpoints
 */
export type DriverApiPaths = DriverAuthPaths &
  DriverProfilePaths &
  DriverRoutesPaths &
  DriverStopsPaths &
  DriverNavigationPaths &
  DriverDispatchPaths &
  DriverHistoryPaths;

/**
 * Combined paths for customer app API
 * Includes all customer-specific endpoints
 */
export type CustomerApiPaths = CustomerAuthPaths &
  CustomerOrdersReadPaths &
  CustomerOrdersCreatePaths &
  CustomerOrdersActionsPaths &
  CustomerInvoicingPaths &
  CustomerSettingsPaths &
  CustomerOrganizationsPaths;

// ─────────────────────────────────────────────────────────────────────────────
// Client Factory Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a type-safe client for the core API (cfleet-core-server)
 *
 * @example
 * ```ts
 * const client = createCoreClient();
 *
 * // Login
 * const { data, error } = await client.POST('/auth/login', {
 *   body: { email: 'user@example.com', password: 'secret' }
 * });
 *
 * // Get fleet vehicles
 * const { data: vehicles } = await client.GET('/vehicles');
 * ```
 */
export function createCoreClient(config: ClientConfig = defaultCoreConfig) {
  return createClient<CoreApiPaths>({
    baseUrl: config.baseUrl,
    headers: config.headers,
  });
}

/**
 * Creates a type-safe client for the optimization API (optimization-processor)
 *
 * @example
 * ```ts
 * const client = createOptimizationClient();
 *
 * // Submit optimization job
 * const { data, error } = await client.POST('/optimization/jobs', {
 *   body: { routes: [...], constraints: {...} }
 * });
 * ```
 */
export function createOptimizationClient(config: ClientConfig = defaultOptimizationConfig) {
  return createClient<OptimizationPaths>({
    baseUrl: config.baseUrl,
    headers: config.headers,
  });
}

/**
 * Creates a type-safe client for the driver app API
 * Uses the core server but with driver-specific paths
 *
 * @example
 * ```ts
 * const client = createDriverClient();
 *
 * // Driver login
 * const { data, error } = await client.POST('/driver/auth/login', {
 *   body: { phone: '+1234567890', pin: '1234' }
 * });
 *
 * // Get assigned routes
 * const { data: routes } = await client.GET('/driver/routes');
 * ```
 */
export function createDriverClient(config: ClientConfig = defaultCoreConfig) {
  return createClient<DriverApiPaths>({
    baseUrl: config.baseUrl,
    headers: config.headers,
  });
}

/**
 * Creates a type-safe client for the customer app API
 * Uses the core server but with customer-specific paths
 *
 * @example
 * ```ts
 * const client = createCustomerClient();
 *
 * // Customer login
 * const { data, error } = await client.POST('/customer/auth/login', {
 *   body: { email: 'customer@example.com', password: 'secret' }
 * });
 *
 * // Get customer orders
 * const { data: orders } = await client.GET('/customer/orders');
 * ```
 */
export function createCustomerClient(config: ClientConfig = defaultCoreConfig) {
  return createClient<CustomerApiPaths>({
    baseUrl: config.baseUrl,
    headers: config.headers,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Authenticated Client Factory Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a type-safe core API client with authentication middleware.
 *
 * The client automatically:
 * - Attaches Bearer token to all requests
 * - Handles 401 responses by refreshing the token
 * - Retries the original request after successful refresh
 * - Calls onAuthFailure when refresh fails
 *
 * @example
 * ```ts
 * import { createAuthenticatedCoreClient, setTokens } from '@cfleet/api-client';
 *
 * const client = createAuthenticatedCoreClient({
 *   baseUrl: 'http://localhost:8081/api/v1',
 *   onAuthFailure: () => {
 *     // Redirect to login
 *     window.location.href = '/login';
 *   },
 *   onTokenRefresh: (tokens) => {
 *     // Optionally sync with external auth state
 *     console.log('Tokens refreshed');
 *   },
 * });
 *
 * // After login, store tokens for automatic attachment
 * const { data } = await client.POST('/auth/login', { body: credentials });
 * if (data) {
 *   setTokens(data.token, data.refresh_token, data.expires_in);
 * }
 *
 * // Subsequent requests automatically include Authorization header
 * const { data: user } = await client.GET('/users/me');
 * ```
 */
export function createAuthenticatedCoreClient(authConfig: AuthMiddlewareConfig) {
  const client = createClient<CoreApiPaths>({
    baseUrl: authConfig.baseUrl,
  });
  client.use(createAuthMiddleware(authConfig));
  return client;
}

/**
 * Creates a type-safe optimization API client with authentication middleware.
 *
 * @see createAuthenticatedCoreClient for usage details
 */
export function createAuthenticatedOptimizationClient(authConfig: AuthMiddlewareConfig) {
  const client = createClient<OptimizationPaths>({
    baseUrl: authConfig.baseUrl,
  });
  client.use(createAuthMiddleware(authConfig));
  return client;
}

/**
 * Creates a type-safe driver API client with authentication middleware.
 *
 * @see createAuthenticatedCoreClient for usage details
 */
export function createAuthenticatedDriverClient(authConfig: AuthMiddlewareConfig) {
  const client = createClient<DriverApiPaths>({
    baseUrl: authConfig.baseUrl,
  });
  client.use(createAuthMiddleware(authConfig));
  return client;
}

/**
 * Creates a type-safe customer API client with authentication middleware.
 *
 * @see createAuthenticatedCoreClient for usage details
 */
export function createAuthenticatedCustomerClient(authConfig: AuthMiddlewareConfig) {
  const client = createClient<CustomerApiPaths>({
    baseUrl: authConfig.baseUrl,
  });
  client.use(createAuthMiddleware(authConfig));
  return client;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pre-configured Client Instances (for simple use cases)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pre-configured core API client instance
 * For simple use cases where custom configuration is not needed
 *
 * Note: For authenticated requests, use createAuthenticatedCoreClient() instead
 */
export const coreClient = createCoreClient();

/**
 * Pre-configured optimization API client instance
 * For simple use cases where custom configuration is not needed
 */
export const optimizationClient = createOptimizationClient();

/**
 * Pre-configured driver API client instance
 * For simple use cases where custom configuration is not needed
 */
export const driverClient = createDriverClient();

/**
 * Pre-configured customer API client instance
 * For simple use cases where custom configuration is not needed
 */
export const customerClient = createCustomerClient();
